import * as React from 'react';
import { useMemo, useState } from 'react';
import { Archive, Edit } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { DataTableColumn, RowAction, SimplePagination } from '@klareospace/pharos-lib-web-utils';
import {
  createActionsColumn,
  DataTable,
  RowActionsMenu,
  useDataTablePagination,
} from '@klareospace/pharos-lib-web-utils';

import type { HandlingUnitType } from '@klareospace/pharos-lib-types';
import { RequirePermission, usePermissions } from '@klareospace/pharos-lib-front-utils';

import { ArchiveHandlingUnitTypeDialog } from './archive-handling-unit-type-dialog';
import { HandlingUnitTypeDrawer } from './handling-unit-type-drawer';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface HandlingUnitTypesTableProps {
  handlingUnitTypes: HandlingUnitType[];
  isLoading: boolean;
  pagination: SimplePagination;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function HandlingUnitTypesTable({
  handlingUnitTypes,
  isLoading,
  pagination,
}: HandlingUnitTypesTableProps): React.JSX.Element {
  const { hasPermission } = usePermissions();
  const { t: tCommon } = useTranslation('common');
  const { t } = useTranslation('settings');

  const [editingHandlingUnitType, setEditingHandlingUnitType] = useState<HandlingUnitType | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<string>('');

  const getRowActions = useMemo(
    () =>
      (handlingUnitType: HandlingUnitType): RowAction[] => {
        const actions: RowAction[] = [
          {
            icon: <Edit className="h-4 w-4" />,
            label: t('handlingUnitTypes.actions.edit'),
            onClick: (): void => setEditingHandlingUnitType(handlingUnitType),
            requiredPermission: 'handling-unit-types:manage',
          },
          {
            className: 'text-red-600',
            hasSeparator: true,
            icon: <Archive className="h-4 w-4" />,
            label: t('handlingUnitTypes.actions.archive.label'),
            onClick: (): void => setArchiveTarget(handlingUnitType.id),
            requiredPermission: 'handling-unit-types:manage',
          },
        ];

        return actions.filter((action) => !action.requiredPermission || hasPermission(action.requiredPermission));
      },
    [hasPermission, t],
  );

  const columns = useMemo<DataTableColumn<HandlingUnitType>[]>(
    () => [
      {
        cell: (hut): React.ReactNode => (
          <RequirePermission
            permission="handling-unit-types:manage"
            fallback={<span className="font-medium">{hut.label}</span>}
          >
            <button
              className="cursor-pointer font-medium hover:underline"
              onClick={(): void => setEditingHandlingUnitType(hut)}
              type="button"
            >
              {hut.label}
            </button>
          </RequirePermission>
        ),
        cellClassName: 'truncate',
        className: 'w-[25%] min-w-[160px]',
        header: t('handlingUnitTypes.table.columns.label'),
        key: 'label',
      },
      {
        cell: (hut): React.ReactNode => <span className="font-mono text-xs text-muted-foreground">{hut.code}</span>,
        cellClassName: 'truncate',
        className: 'w-[25%] min-w-[120px]',
        header: t('handlingUnitTypes.table.columns.code'),
        key: 'code',
      },
      {
        cell: (hut): React.ReactNode => `${String(hut.weight.value)} ${tCommon(`units.${hut.weight.unit}`)}`,
        className: 'w-[25%] min-w-[120px]',
        header: t('handlingUnitTypes.table.columns.weight'),
        key: 'weight',
      },
      {
        cell: (hut): React.ReactNode => `${String(hut.volume.value)} ${tCommon(`units.${hut.volume.unit}`)}`,
        className: 'w-[25%] min-w-[120px]',
        header: t('handlingUnitTypes.table.columns.volume'),
        key: 'volume',
      },
      createActionsColumn<HandlingUnitType>((hut) => {
        const actions = getRowActions(hut);
        if (actions.length === 0) return null;
        return <RowActionsMenu actions={actions} ariaLabel={t('handlingUnitTypes.table.columns.actions')} />;
      }),
    ],
    [getRowActions, t],
  );

  const tablePagination = useDataTablePagination({
    labels: {
      nextPage: t('handlingUnitTypes.table.nextPage'),
      pageLabel: (current, total): string => t('handlingUnitTypes.table.pageLabel', { current, total }),
      previousPage: t('handlingUnitTypes.table.previousPage'),
      showingText: (startItem, endItem, totalCount): string =>
        t('handlingUnitTypes.table.showing', { from: startItem, to: endItem, total: totalCount }),
    },
    pagination,
  });

  return (
    <>
      <DataTable<HandlingUnitType>
        ariaLabel={t('handlingUnitTypes.table.ariaLabel')}
        columns={columns}
        emptyMessage={t('handlingUnitTypes.table.empty')}
        isLoading={isLoading}
        pagination={tablePagination}
        rowKey={(hut): string => hut.id}
        rows={handlingUnitTypes}
      />
      <HandlingUnitTypeDrawer
        handlingUnitType={editingHandlingUnitType}
        onClose={(): void => setEditingHandlingUnitType(null)}
        open={editingHandlingUnitType !== null}
      />
      <ArchiveHandlingUnitTypeDialog handlingUnitTypeId={archiveTarget} onClose={(): void => setArchiveTarget('')} />
    </>
  );
}
